// Code generated by tygo. DO NOT EDIT.
// GENERATED by tygo from api/pkg/types — do not edit by hand.
// Regenerate with: ./stack generate_types

//////////
// source: enums.go

export type StorageDriver = number /* int */;
export const StorageDriverNone: StorageDriver = 0;
export const StorageDriverLocalFS: StorageDriver = 1;
export const StorageDriverGCS: StorageDriver = 2;

//////////
// source: types.go

/**
 * Participant represents a speaker/participant in a video.
 */
export interface Participant {
  id: string;
  name: string;
  color: string;
  position: number /* float64 */;
  avatar_url?: string;
}
/**
 * SubtitleInfo tracks which subtitle languages exist for a video.
 * The master language carries speaker attribution; other languages are
 * derived translations with identical cue structure.
 */
export interface SubtitleInfo {
  master_language: string;
  languages: string[];
}
/**
 * Video represents a video whose streams are hosted externally (Bunny.net).
 * Stored as JSON in GCS metadata.json files.
 */
export interface Video {
  id: string;
  name: string;
  description: string;
  series?: string;
  episode?: string;
  created_at: string;
  state: string; // "created" | "ready"
  /**
   * Pasted HLS stream URL (Bunny.net). Non-empty <=> state "ready".
   */
  stream_url?: string;
  participants?: Participant[];
  /**
   * Language is the spoken language of the video (ISO code, e.g. "de").
   * Diarization saves generated master cues under it; defaults to "en".
   */
  language?: string;
  /**
   * Subtitles (master cue model — cue JSON stored per language in GCS)
   */
  subtitles?: SubtitleInfo;
  subtitle_offset?: number /* float64 */;
  /**
   * Speaker attribution — two editable layers plus the derived player map.
   * VoiceGroups (Layer 1) is diarization's stable clustering.
   * SpeakerLabels (Layer 2) maps each voice group to a participant.
   * AttributionOverrides fixes individual mis-grouped cues.
   * SpeakerAttributions is DERIVED from the three above and consumed by the player.
   */
  voice_groups?: VoiceGroup[];
  speaker_labels?: { [key: string]: string}; // voiceGroupTag → participantID
  attribution_overrides?: { [key: string]: string}; // cueIndex → participantID
  speaker_attributions?: { [key: string]: string}; // DERIVED cueIndex → participantID
  /**
   * Diarization (speaker attribution via STT)
   */
  diarization_state?: string; // "" | "diarizing" | "diarized" | "diarize_failed"
  diarization_error?: string;
  diarization_job_name?: string;
  /**
   * Translation (per-language subtitle generation via Anthropic)
   */
  translation_state?: string; // "" | "translating" | "translated" | "translate_failed"
  translation_error?: string;
  translation_job?: string; // current/last translate job ID
  translated_at?: { [key: string]: string}; // lang → last translated time
  /**
   * Languages whose cues were restructured by a split or join of the master
   * (data/overlap.go) and need translating again. Publishing is refused
   * until it is empty.
   */
  translations_stale?: string[];
  /**
   * Publication (content integrity manifest + lifecycle)
   */
  publication_state?: string; // "" | "publishing" | "published" | "sealed" | "publish_failed"
  publication_error?: string;
  publication_job?: string;
  publication?: PublicationInfo;
  /**
   * Rotation adjustment (degrees offset to align video with true north)
   */
  rotation_adjustment?: number /* float64 */;
  /**
   * FourView frames the player's All views layout — one panel per
   * participant, two to four — under the name it had when only four panels
   * existed. Presentation only: it is not in the manifest, so it stays
   * editable after the seal. Nil means the player's defaults.
   */
  four_view?: FourViewFraming;
  /**
   * StartTilt is where the 360 camera points up or down when a video opens,
   * in degrees: 0 is the horizon, negative looks down. Rotation adjustment
   * turns the sphere left/right; this tilts the starting camera instead,
   * because tilting the sphere would tip the horizon once a viewer pans.
   * Presentation only, like FourView: outside the manifest, editable after
   * the seal.
   */
  start_tilt?: number /* float64 */;
  /**
   * Thumbnail
   */
  thumbnail_url?: string;
  /**
   * Job history
   */
  job_history?: JobHistoryEntry[];
}
/**
 * FourViewFraming is how the player's All views layout frames its people:
 * each panel's width over its height, and how far each panel's camera zooms in
 * from the normal view (1 = not at all). Zero means "the player's default".
 */
export interface FourViewFraming {
  pane_aspect?: number /* float64 */;
  zoom?: number /* float64 */;
}
/**
 * JobHistoryEntry records a single worker job attempt for a video.
 */
export interface JobHistoryEntry {
  id: string;
  type: string; // "diarize" | "translate" | "verify"
  started_at: string;
  completed_at?: string;
  status: string; // "running", "completed", "failed"
  error?: string;
  detail?: string; // human-readable specifics, e.g. translate target languages
  external_ref?: string; // e.g. AssemblyAI transcript ID
  log_gcs_path?: string;
}
/**
 * Container is a published video's identity over time: the thing a link
 * points at, holding the list of the versions published under it. Each
 * version is an ordinary Video with its own manifest, root hash and on-chain
 * account; the last entry in Versions is the current one.
 * The list is append-only for everything the chain names. Once an entry has
 * a VersionRef anchored at its index, nothing reorders, edits or removes it —
 * a publisher can add, and only add, which is the same promise addenda make
 * about subtitles. An entry no VersionRef names is a weaker thing: deleting
 * its version removes it outright and renumbers the survivors, which is what
 * happens to every unclaimed entry of a container that was never anchored and
 * to the unclaimed, unsealed tail of one that was. Keeping a stamp there
 * instead would put the local index permanently ahead of the count the chain
 * assigns at the next append. See data.planVersionDeletion for the whole
 * rule.
 * Stored as JSON at {namespace}/containers/{id}.json. The ID equals the first
 * version's video UUID, so links and on-chain anchors that predate containers
 * keep working unchanged.
 * Ordering and Private live here rather than on a version because they
 * describe where the video sits in the platform, not what it is: publishing a
 * new version must not shuffle the homepage or silently relist an unlisted
 * video. Neither is ever written to chain — the chain holds nothing
 * curatorial.
 */
export interface Container {
  id: string;
  slug: string; // public URL key; mutable, unique across the store
  /**
   * PrevSlugs keeps every slug this container has previously answered to,
   * so a rename redirects instead of breaking links already in the world.
   */
  prev_slugs?: string[];
  versions: ContainerVersion[];
  /**
   * DraftVideoID is the unpublished next version, if one is being prepared.
   * At most one exists at a time: an in-flight draft is what "publish a new
   * version" produces, and publishing it moves it into Versions.
   */
  draft_video_id?: string;
  /**
   * Ordering controls the position in listings (ascending).
   * 0 = unset — unordered containers sort after ordered ones, newest first.
   */
  ordering?: number /* int */;
  /**
   * Private hides the container from the public listing; it stays reachable
   * by direct link/embed and visible in the admin listing.
   */
  private?: boolean;
  created_at: string;
  /**
   * Chain anchor for the container account itself. Nil until the browser
   * reports a successful init_container transaction.
   */
  chain?: ContainerAnchor;
}
/**
 * ContainerVersion is one entry in a container's version list. Index is
 * assigned at publish and matches the VersionRef account's index on chain
 * once the version is anchored.
 * DeletedAt records that a version's bytes were deleted. Whether the entry
 * itself stays depends on whether the chain names it: once a VersionRef is
 * anchored at its index, removing it would renumber every later index and
 * break the correspondence with the on-chain list, so the entry stays — the
 * version was genuinely published, and that it can no longer be played does
 * not unpublish it. An entry no VersionRef names is removed outright and the
 * list renumbered instead of accumulating stamps a future append could never
 * match against the chain's own count. See data.planVersionDeletion for the
 * whole rule, including which unclaimed entries that covers.
 */
export interface ContainerVersion {
  index: number /* int */;
  video_id: string;
  added_at: string;
  deleted_at?: string;
  chain?: VersionRefAnchor; // nil until anchored on chain
}
/**
 * ContainerAnchor is the API's record of a container having been created on
 * the Solana program (chain/programs/forum's init_container). Like
 * ChainAnchor and AddendumAnchor, it is a cache of what a browser reported,
 * NOT an independent verification — this server never reads a Solana RPC, so
 * nothing here has been checked against the chain.
 * CreatedAt is the CHAIN's clock, read back off the account and stamped by
 * the program when the transaction landed, never our own. VersionCount is
 * likewise what the account held when it was last read back; a browser that
 * wants the truth reads the chain, which is the whole point of anchoring the
 * version list in the first place.
 */
export interface ContainerAnchor {
  cluster: string; // "localnet" | "devnet" | "mainnet-beta"
  program_id: string;
  publisher_pda: string;
  container_pda: string;
  authority: string; // the wallet that signed
  signature: string; // the most recent transaction
  version_count: number /* int */; // read back from the account
  created_at: string; // the CHAIN's clock, not ours
  recorded_at: string; // when THIS SERVER read the account back
}
/**
 * VersionRefAnchor records that a version was appended to its container's
 * on-chain list (chain/programs/forum's append_version). Like every other
 * anchor type here it is a cache of what a browser reported, not an
 * independent verification.
 * Index is the slot the append claimed, taken from the container's
 * version_count at that instant, and AddedAt is the CHAIN's clock off the
 * VersionRef account.
 * VideoID names the version the report is about, and is required. Without it
 * a report is addressed by index alone, so ANY pre-existing divergence
 * between the local list and the chain's silently files the anchor against
 * whichever entry happens to sit at that number — recording, permanently,
 * that a version was appended when it was not. With it, the same divergence
 * becomes a refusal at the moment it would first do damage.
 */
export interface VersionRefAnchor {
  version_pda: string;
  index: number /* int */;
  video_id: string; // which version this report is about
  signature: string;
  added_at: string; // the CHAIN's clock
}
/**
 * Publication lifecycle states. A video moves draft("") -> publishing ->
 * published -> sealed, with publish_failed as the dead end of a failed
 * publish/republish. See api/pkg/data/publication.go's IsSealed, which is
 * the one predicate every guard must use to tell published from sealed.
 * Sealed is reached only by an explicit seal; a published video stays
 * correctable, with no deadline, until then.
 */
export const PublicationStatePublishing = "publishing";
/**
 * Publication lifecycle states. A video moves draft("") -> publishing ->
 * published -> sealed, with publish_failed as the dead end of a failed
 * publish/republish. See api/pkg/data/publication.go's IsSealed, which is
 * the one predicate every guard must use to tell published from sealed.
 * Sealed is reached only by an explicit seal; a published video stays
 * correctable, with no deadline, until then.
 */
export const PublicationStatePublishFailed = "publish_failed";
/**
 * Publication lifecycle states. A video moves draft("") -> publishing ->
 * published -> sealed, with publish_failed as the dead end of a failed
 * publish/republish. See api/pkg/data/publication.go's IsSealed, which is
 * the one predicate every guard must use to tell published from sealed.
 * Sealed is reached only by an explicit seal; a published video stays
 * correctable, with no deadline, until then.
 */
export const PublicationStatePublished = "published";
/**
 * Publication lifecycle states. A video moves draft("") -> publishing ->
 * published -> sealed, with publish_failed as the dead end of a failed
 * publish/republish. See api/pkg/data/publication.go's IsSealed, which is
 * the one predicate every guard must use to tell published from sealed.
 * Sealed is reached only by an explicit seal; a published video stays
 * correctable, with no deadline, until then.
 */
export const PublicationStateSealed = "sealed";
/**
 * Chain anchor states, mirroring VideoState on chain
 * (chain/programs/forum/src/lib.rs). Deliberately a smaller set than
 * PublicationState* above: the chain has no "publishing" or "publish_failed"
 * — those are local job bookkeeping with no on-chain existence — so an
 * anchor is recorded only once the chain itself has something to say.
 */
export const ChainStatePublished = "published";
/**
 * Chain anchor states, mirroring VideoState on chain
 * (chain/programs/forum/src/lib.rs). Deliberately a smaller set than
 * PublicationState* above: the chain has no "publishing" or "publish_failed"
 * — those are local job bookkeeping with no on-chain existence — so an
 * anchor is recorded only once the chain itself has something to say.
 */
export const ChainStateSealed = "sealed";
/**
 * PublicationInfo summarises a video's verification manifest and where it
 * sits in the publication lifecycle. RootHash is the hash of the stored
 * root.json bytes — the value anchored on-chain — so the stored file must be
 * served verbatim and never re-serialized.
 */
export interface PublicationInfo {
  root_hash: string;
  hash_algo: string;
  manifest_path: string;
  source_url: string; // playlist the manifest was built from
  rendition_count: number /* int */;
  segment_count: number /* int */;
  total_bytes: number /* int64 */;
  /**
   * Lifecycle. Mirrors the Video account in chain/programs/forum (see
   * docs/plans/2026-08-11-forum-program-design.md §4.2) field for field, so
   * the local state machine and the on-chain one cannot silently drift once
   * phase 5 makes the chain the enforcer.
   * SealedLangs is every subtitle language the manifest covers, taken from
   * the manifest itself rather than from Video.Subtitles — a language added
   * after publishing is in the video but not in what was hashed. This is
   * the list handed to publish_video/republish_video as `sealed_langs`, and
   * the thing add_subtitle_track refuses to shadow, so it has to describe
   * the manifest and nothing else.
   */
  sealed_langs?: string[];
  revision: number /* int */; // increments on every republish; 1 on first publish
  published_at: string; // the most recent publish or republish
  sealed_at: string; // set only by an explicit seal; zero while unsealed
  /**
   * Chain anchor. Nil until the browser reports a successful publish_video
   * or seal_video transaction — see data.VideoStore.RecordChainAnchor and
   * server.AnchorVideo. This is a cache of what the chain last told us, not
   * a live read of it: see ChainAnchor's doc comment for the trust boundary.
   */
  chain?: ChainAnchor;
  /**
   * Host verification (re-verify job result). Purely informational: it
   * never gates the seal, which is why it can be meaningfully absent even
   * on a long-published video that has just never been re-verified.
   */
  host_verified_at?: string;
  host_verified_url?: string;
  /**
   * Addenda are subtitle languages added AFTER the video was sealed. See
   * AddendumTrack's doc comment for why these are never merged into
   * SealedLangs or the manifest the root hash covers.
   */
  addenda?: AddendumTrack[];
  /**
   * StorageAnchors record places the video's bytes were reported to be
   * fetchable from, each backed by its own add_storage_location account on
   * chain. Unlike Addenda these are never gated on the seal — see
   * StorageAnchor's doc comment.
   */
  storage_anchors?: StorageAnchor[];
}
/**
 * ChainAnchor is the API's record of a video's root hash having been
 * anchored on the Solana program (chain/programs/forum) — the account
 * addresses, the signature that landed it, and what the account held the
 * moment we last read it back. It is a convenience cache for the admin UI,
 * not a verification: this server never talks to a Solana RPC, so nothing
 * here is independently checked against the chain. See
 * server.AnchorVideo's doc comment for the trust boundary this implies.
 * PublishedAt is the CHAIN's clock, not ours: it is read back off the
 * on-chain account, stamped by the program at the instant the publisher's
 * transaction landed, not by our worker when it finished building the
 * manifest minutes or hours earlier. RecordChainAnchor copies it onto
 * PublicationInfo.PublishedAt so the admin UI shows when the publication
 * actually landed on chain, not when our worker happened to finish.
 */
export interface ChainAnchor {
  cluster: string; // "localnet" | "devnet" | "mainnet-beta"
  program_id: string;
  publisher_pda: string;
  video_pda: string;
  authority: string; // the wallet that signed
  signature: string; // the most recent transaction
  revision: number /* int */; // read back from the account
  root_hash: string; // read back from the account
  state: string; // ChainStatePublished | ChainStateSealed
  published_at: string; // the CHAIN's clock, not ours — see doc comment above
  sealed_at: string; // zero unless State == ChainStateSealed
  recorded_at: string; // when THIS SERVER read the account back
}
/**
 * AddendumTrack is one subtitle language added AFTER the video was sealed.
 * It is never part of the sealed base: the base is frozen by the root hash,
 * and an addendum carries its own hash anchored in its own on-chain account.
 */
export interface AddendumTrack {
  lang: string; // BCP-47, max 8 bytes on chain
  hash: string; // lowercase hex sha256 of the stored snapshot bytes
  path: string; // "verification/addenda/<lang>.json"
  cue_count: number /* int */;
  created_at: string; // when WE built it
  chain?: AddendumAnchor; // nil until anchored on chain
}
/**
 * AddendumAnchor records that an AddendumTrack was written to the chain.
 * Like ChainAnchor, this is a cache of what a browser reported, not an
 * independent verification — this server never reads a Solana RPC.
 */
export interface AddendumAnchor {
  subtitle_pda: string;
  signature: string;
  added_at: string; // the CHAIN's clock, off SubtitleTrack.added_at
}
/**
 * StorageAnchor records that a place the video's bytes can be fetched from was
 * written to the chain (chain/programs/forum's add_storage_location). Like
 * ChainAnchor and AddendumAnchor, this is a cache of what a browser reported,
 * NOT an independent verification — this server never reads a Solana RPC, so
 * nothing here is checked against the chain before being stored.
 * Unlike an addendum, a storage location is never gated on the video's seal
 * state: the program allows add_storage_location forever, on a sealed video
 * or not, because adding *where* bytes are found never changes *what* they
 * are. data.VideoStore.RecordStorageAnchor mirrors that — it has no seal
 * guard, on purpose.
 */
export interface StorageAnchor {
  index: number /* uint16 */; // the on-chain storage_count slot it claimed
  driver: number /* uint8 */; // 1 = https, 2 = ipfs, 3 = filecoin
  location: string; // max 256 bytes, matching the program
  storage_pda: string;
  signature: string;
  added_at: string; // the CHAIN's clock
}
/**
 * VoiceGroup is one persisted cluster of cues that diarization attributed to a
 * single voice ("the same voice said these lines"). The grouping is stable and
 * never overwritten by labeling; who the voice actually *is* lives in
 * Video.SpeakerLabels.
 */
export interface VoiceGroup {
  tag: string; // diarization speaker tag, e.g. "A", "B"
  sample_texts: string[]; // first few lines, for identification
  cue_indices: number /* int */[]; // 0-based cue indices in this group
  line_count: number /* int */; // number of cues in this group
}
